/**
 * DeckWarnings - Deck health warnings display
 *
 * Shows actionable warnings about deck construction issues
 * like low lands, heavy curve, missing removal, etc.
 */
import type { ReactElement } from 'react';
import { useState } from 'react';

import clsx from 'clsx';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';
import { TbExternalLink } from 'react-icons/tb';

import InfoButton, { InfoList, InfoSection, InfoTip } from '@/components/InfoButton/InfoButton';

import type { DeckWarning, WarningSeverity, WarningType } from '../../hooks/useDeckHealth';

/** Stats section IDs that warnings can link to */
export type StatsSectionId =
  'summary' | 'validation' | 'manaCurve' | 'types' | 'colors' | 'landAnalysis' | 'deckHealth' | 'commanderCategories';

/** Mapping from warning types to relevant stats section */
const WARNING_SECTION_MAP: Record<WarningType, StatsSectionId | null> = {
  'low-lands': 'landAnalysis',
  'high-lands': 'landAnalysis',
  'color-sources': 'landAnalysis',
  'heavy-curve': 'manaCurve',
  'no-removal': 'commanderCategories',
  'no-card-draw': 'commanderCategories',
  'no-ramp': 'commanderCategories',
  'limited-low-creatures': 'types',
  'limited-low-two-drops': 'manaCurve',
  'low-mana-fixing': 'landAnalysis',
};

export interface DeckWarningsProps {
  warnings: DeckWarning[];
  /** Compact mode for left panel */
  compact?: boolean;
  /** Callback to navigate to a stats section */
  onNavigateToSection?: (sectionId: StatsSectionId) => void;
}

const SEVERITY_CONFIG: Record<WarningSeverity, { icon: string; colors: string; borderColor: string }> = {
  critical: {
    icon: '🔴',
    colors: 'text-red-400 bg-red-500/10',
    borderColor: 'border-red-500/30',
  },
  warning: {
    icon: '⚠️',
    colors: 'text-amber-400 bg-amber-500/10',
    borderColor: 'border-amber-500/30',
  },
  info: {
    icon: '💡',
    colors: 'text-sky-400 bg-sky-500/10',
    borderColor: 'border-sky-500/30',
  },
};

function DeckWarningsInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What are these?">
        <p>
          Deck health warnings analyze your deck composition and flag common issues that could affect consistency or power. These are
          suggestions, not rules—some strategies intentionally break these guidelines.
        </p>
      </InfoSection>

      <InfoSection title="Warning types">
        <InfoList
          items={[
            'Land count: Too few or too many lands for your curve',
            'Heavy curve: High average mana value that may slow you down',
            'Limited removal: Not enough ways to interact with threats',
            'Card draw: Insufficient ways to maintain card advantage',
            'Ramp: Missing mana acceleration (especially for Commander)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Severity levels">
        <InfoList
          items={[
            '🔴 Critical: Likely to significantly impact gameplay',
            '⚠️ Warning: Worth addressing but deck can still function',
            '💡 Info: Minor optimization suggestion',
          ]}
        />
      </InfoSection>

      <InfoTip>
        Click on any warning to expand it and see specific suggestions. These are based on general deck building heuristics—always consider
        your specific strategy and meta.
      </InfoTip>
    </>
  );
}

interface WarningItemProps {
  warning: DeckWarning;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigateToSection?: (sectionId: StatsSectionId) => void;
}

function WarningItem({ warning, isExpanded, onToggle, onNavigateToSection }: WarningItemProps): ReactElement {
  const config = SEVERITY_CONFIG[warning.severity];
  const linkedSection = WARNING_SECTION_MAP[warning.type];
  const canNavigate = linkedSection && onNavigateToSection;

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canNavigate) {
      onNavigateToSection(linkedSection);
    }
  };

  return (
    <div className={clsx('rounded-lg border', config.borderColor, config.colors)}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-2 p-3 text-left transition-colors hover:bg-white/5"
        aria-expanded={isExpanded}
      >
        <span className="mt-0.5 shrink-0 text-sm">{config.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{warning.title}</span>
            {isExpanded ? (
              <IoChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
            ) : (
              <IoChevronForward className="h-3.5 w-3.5 shrink-0 opacity-60" />
            )}
          </div>
          {warning.stats && !isExpanded && (
            <span className="mt-0.5 block text-xs opacity-70">
              {warning.stats.current} / {warning.stats.recommended} {warning.stats.label}
            </span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-inherit px-3 pt-2 pb-3">
          <p className="text-xs opacity-80">{warning.description}</p>
          <div className="mt-2 rounded bg-black/20 p-2">
            <span className="text-xs font-medium">Suggestion:</span>
            <p className="mt-0.5 text-xs opacity-80">{warning.suggestion}</p>
          </div>
          {warning.stats && (
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="opacity-60">Current: {warning.stats.current}</span>
              <span className="opacity-60">Recommended: {warning.stats.recommended}</span>
            </div>
          )}
          {canNavigate && (
            <button
              type="button"
              onClick={handleNavigate}
              className="mt-2 flex items-center gap-1 text-xs font-medium opacity-70 transition-opacity hover:opacity-100"
            >
              <TbExternalLink className="h-3 w-3" />
              View in details
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function DeckWarnings({ warnings, compact = true, onNavigateToSection }: DeckWarningsProps): ReactElement {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Sort warnings by severity (critical first)
  const sortedWarnings = [...warnings].sort((a, b) => {
    const order: Record<WarningSeverity, number> = { critical: 0, warning: 1, info: 2 };
    return order[a.severity] - order[b.severity];
  });

  if (warnings.length === 0) {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
        <div className="flex items-center justify-center gap-1.5">
          <span className="text-sm font-medium text-emerald-400">✓ No warnings</span>
          <InfoButton title="Deck Health Warnings">
            <DeckWarningsInfo />
          </InfoButton>
        </div>
        <p className="mt-1 text-xs text-emerald-300/70">Your deck looks well-balanced</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-ash text-xs font-medium tracking-wider uppercase">
          {warnings.length} {warnings.length === 1 ? 'Warning' : 'Warnings'}
        </span>
        <InfoButton title="Deck Health Warnings">
          <DeckWarningsInfo />
        </InfoButton>
      </div>

      <div className={clsx('space-y-2', compact && 'max-h-64 overflow-y-auto pr-1')}>
        {sortedWarnings.map((warning) => (
          <WarningItem
            key={warning.type}
            warning={warning}
            isExpanded={expandedId === warning.type}
            onToggle={() => setExpandedId(expandedId === warning.type ? null : warning.type)}
            onNavigateToSection={onNavigateToSection}
          />
        ))}
      </div>
    </div>
  );
}
